// LIVEMOA · 사용자 데이터 삭제 안내 (Meta App Review 요구사항 대응)
// 공개 페이지 · 로그인 불필요 · Meta Portal "User Data Deletion Instructions URL" 입력용
// 기존 Privacy/Terms 구조 재사용 · BusinessInfo 재사용.
import React from 'react';
import { Link } from 'react-router-dom';
import { BusinessInfo } from './Terms';

const H2 = ({ children, id }) => (
  <h2 id={id} className="mt-10 mb-3 text-xl font-bold text-black dark:text-white">
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="mt-6 mb-2 text-base font-semibold text-black dark:text-white">{children}</h3>
);
const P = ({ children }) => (
  <p className="my-2 text-sm leading-7 text-gray-700 dark:text-gray-300">{children}</p>
);
const UL = ({ children }) => (
  <ul className="my-2 ml-5 list-disc space-y-1 text-sm leading-7 text-gray-700 dark:text-gray-300">
    {children}
  </ul>
);
const OL = ({ children }) => (
  <ol className="my-2 ml-5 list-decimal space-y-1 text-sm leading-7 text-gray-700 dark:text-gray-300">
    {children}
  </ol>
);

export default function DataDeletion() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="data-deletion-page">
      <main>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <header className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-black dark:text-white">
              LIVEMOA 사용자 데이터 삭제 안내
            </h1>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              시행일: 2026년 2월 19일 · 운영자: 엠지컴퍼니 (LIVEMOA · 라이브모아)
            </p>
            <p className="mt-3 text-sm leading-7 text-gray-700 dark:text-gray-300">
              본 페이지는 LIVEMOA(라이브모아)가 외부 플랫폼(Instagram · Meta Platforms, Inc. ·
              YouTube · TikTok · Kakao 등)과 연결하여 처리하는 이용자 데이터의 <b>연결 해제 및 삭제
              요청 방법</b>을 안내합니다. 상세한 개인정보 처리 원칙은{' '}
              <Link to="/privacy" className="underline hover:text-[#FF3B30]" data-testid="deletion-to-privacy">
                개인정보처리방침
              </Link>{' '}
              을 참고해주세요.
            </p>
          </header>

          <H2 id="d1">1. 삭제 대상 데이터 (Instagram · Meta 연동 기준)</H2>
          <P>
            이용자가 LIVEMOA 에 Instagram Business 계정을 연결한 경우, 회사는 다음 항목을 보유합니다.
            연결 해제 및 삭제 요청 시 아래 항목이 파기됩니다.
          </P>
          <UL>
            <li>OAuth 연결 정보 (Access Token · Refresh Token · 만료 시각 · 암호화 저장)</li>
            <li>Instagram Business 계정 매핑 (Instagram 사용자 ID · username · account_type)</li>
            <li>연결 시 부여된 권한 스코프 목록 (예: <code>instagram_business_basic</code>,{' '}
              <code>instagram_business_manage_comments</code>)
            </li>
            <li>Webhook 구독 상태 (<code>live_comments</code> 등 구독 필드 · 최근 subscribe 결과)</li>
            <li>연동을 위해 저장된 채널 식별자 · 자동수집 설정값 · 최근 스캔 로그</li>
            <li>LIVE 방송 세션 매핑 (Instagram → LIVEMOA broadcast · 서버 내부 세션 식별자)</li>
          </UL>
          <P>
            YouTube · TikTok · Kakao 등 다른 플랫폼 연결 정보도 동일한 원칙으로 처리됩니다.
          </P>

          <H2 id="d2">2. 연결 해제 방법 (권장 · 즉시 처리)</H2>
          <P>
            이용자가 직접 아래 절차로 즉시 연결을 해제할 수 있으며, 해제 즉시 관련 OAuth 토큰과
            연동 상태는 서비스 내에서 삭제됩니다.
          </P>
          <H3>2-1) LIVEMOA 판매자 콘솔에서 해제</H3>
          <OL>
            <li>LIVEMOA 로그인 → 판매자 대시보드 → 라이브 컨트롤 (<code>/seller/live-control</code>)</li>
            <li>연결된 Instagram/YouTube/TikTok 계정 카드에서 <b>"연결 해제"</b> 버튼 클릭</li>
            <li>즉시 서버 측 OAuth 토큰이 삭제되고 관련 자동수집 작업이 중단됩니다.</li>
          </OL>
          <H3>2-2) 외부 플랫폼 계정 관리 페이지에서 해제</H3>
          <UL>
            <li>
              <b>Instagram / Meta</b>: Instagram 앱 → 설정 → 웹사이트 권한 → 앱 및 웹사이트 →
              LIVEMOA 삭제
              <br />또는 <a href="https://accountscenter.meta.com/" target="_blank" rel="noreferrer" className="underline">Meta 계정센터</a> → 비즈니스 도구 및 계정에서 액세스 권한 취소
            </li>
            <li>
              <b>YouTube / Google</b>: <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="underline">Google 계정 → 타사 앱 액세스</a> → LIVEMOA 액세스 삭제
            </li>
            <li>
              <b>TikTok</b>: TikTok 앱 → 프로필 → 설정 및 개인정보 보호 → 보안 및 로그인 → 연결된 앱 관리 → LIVEMOA 해제
            </li>
            <li>
              <b>Kakao</b>: <a href="https://accounts.kakao.com/weblogin/account/partner" target="_blank" rel="noreferrer" className="underline">카카오계정 → 연결된 서비스 관리</a> → LIVEMOA 연결 끊기
            </li>
          </UL>

          <H2 id="d3">3. 계정 및 저장 데이터 전체 삭제 요청 (탈퇴 포함)</H2>
          <P>
            OAuth 연결 해제만으로 삭제되지 않는 <b>계정 프로필 · 판매자 정보 · 서비스 이용 로그</b> 등을 포함한
            전체 삭제를 원하는 경우, 아래 방법으로 이용자 데이터 삭제 요청을 접수받고 있습니다.
          </P>
          <H3>3-1) 삭제 요청 접수 방법</H3>
          <UL>
            <li>이메일: <a href="mailto:k615745@naver.com" className="underline">k615745@naver.com</a></li>
            <li>전화: 010-6744-6344 (평일 10:00 ~ 18:00, 주말·공휴일 제외)</li>
            <li>
              메일 제목 예시: <code>[LIVEMOA 데이터 삭제 요청] {'{'}가입 이메일 또는 계정명{'}'}</code>
            </li>
          </UL>
          <H3>3-2) 확인이 필요한 정보</H3>
          <P>
            본인 확인을 위해 요청 시 아래 정보를 함께 알려주시기 바랍니다. 회사는 확인 절차 완료 후
            지체 없이 삭제를 진행합니다 (통상 접수일로부터 영업일 기준 7일 이내).
          </P>
          <UL>
            <li>LIVEMOA 가입 이메일 또는 소셜 로그인 식별 정보 (예: 카카오 닉네임)</li>
            <li>연결된 외부 플랫폼 계정 이름 (예: Instagram username)</li>
            <li>삭제 요청 범위 (전체 탈퇴 / 특정 외부 플랫폼 연동 데이터만)</li>
          </UL>

          <H2 id="d4">4. 삭제 예외 — 법령상 보존 의무</H2>
          <P>
            아래 항목은 관계 법령에 따라 이용자의 삭제 요청이 있더라도 <b>법정 기간 동안 보존</b>됩니다.
            보존 기간이 경과하면 즉시 파기됩니다.
          </P>
          <UL>
            <li>계약·청약철회·소비자 불만·분쟁 처리 기록: 각 3~5년 (전자상거래법)</li>
            <li>결제·정산 관련 기록 (거래 성사 시): 5년 (전자상거래법)</li>
            <li>전자적 접속기록: 3개월 (통신비밀보호법)</li>
          </UL>
          <P className="mt-2">
            결제·정산 관련 개인정보의 경우 회사는 결제 대행사·정산 파트너의 <b>거래 식별자</b>만 보관하며,
            카드번호 · 계좌번호 등 민감정보는 직접 저장하지 않습니다.
          </P>

          <H2 id="d5">5. 처리 결과 통지</H2>
          <P>
            삭제 요청 접수 후 회사는 처리 완료 시 요청자의 이메일 또는 연락처로 결과를 통지합니다.
            법정 보존 대상이 포함되어 부분 삭제만 가능한 경우, 그 사유와 잔존 데이터의 항목·기간을
            함께 안내합니다.
          </P>

          <H2 id="d6">6. Meta 이용자 안내 (Instagram Business 연동 전용)</H2>
          <P>
            Meta Platforms, Inc. 가 요구하는 이용자 데이터 삭제 절차 준수를 위해, LIVEMOA 는 Instagram
            Business Login 을 통해 연결된 이용자 데이터의 삭제 요청을 위 방법으로 접수합니다. 회사는
            자동화된 서버 측 삭제 기능(예: Facebook Login 의 Data Deletion Callback) 은 현재 구현하지 않았으며,
            향후 구현 시 본 페이지에 즉시 반영합니다. 그동안은 이메일·전화 요청 기반의 수동 삭제 절차로
            동일한 결과를 보장합니다.
          </P>

          <BusinessInfo />

          <p className="mt-10 text-xs text-gray-500 dark:text-gray-400">
            관련 문서:{' '}
            <Link to="/privacy" className="underline hover:text-[#FF3B30]" data-testid="deletion-link-privacy">개인정보처리방침</Link>
            {' · '}
            <Link to="/terms" className="underline hover:text-[#FF3B30]" data-testid="deletion-link-terms">이용약관</Link>
          </p>
        </article>
      </main>
    </div>
  );
}
